import { observer } from 'mobx-react-lite';
import LanguageStore from '../../store/LanguageStore';
import './channels-table.scss';

const ChannelsTable = observer(({ 
    channels, 
    pagination, 
    sortBy, 
    sortOrder, 
    onSort, 
    onPageChange,
    loading 
}) => {
    const t = LanguageStore.t;
    const { language } = LanguageStore;

    const formatNumber = (num) => {
        if (!num && num !== 0) return '-';
        
        const number = typeof num === 'string' ? parseInt(num, 10) : num;
        
        if (number >= 1000000) {
            const millions = number / 1000000;
            return millions % 1 === 0 
                ? `${millions}M` 
                : `${millions.toFixed(1)}M`;
        } else if (number >= 1000) {
            const thousands = number / 1000;
            return thousands % 1 === 0 
                ? `${thousands}K` 
                : `${thousands.toFixed(1)}K`;
        }
        
        return number.toLocaleString();
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) {
            return '⇅'; // Нейтральная иконка
        }
        return sortOrder === 'ASC' ? '↑' : '↓';
    };

    const handleSortClick = (field) => {
        onSort(field);
    };

    const renderPagination = () => {
        const pages = [];
        const totalPages = pagination.totalPages;
        const currentPage = pagination.page;

        // Показываем максимум 7 страниц
        let startPage = Math.max(1, currentPage - 3);
        let endPage = Math.min(totalPages, currentPage + 3);

        if (startPage > 1) {
            pages.push(
                <button
                    key={1}
                    onClick={() => onPageChange(1)}
                    className="channels-table__page-btn"
                >
                    1
                </button>
            );
            if (startPage > 2) {
                pages.push(<span key="ellipsis-start" className="channels-table__ellipsis">...</span>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    className={`channels-table__page-btn ${i === currentPage ? 'channels-table__page-btn--active' : ''}`}
                >
                    {i}
                </button>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<span key="ellipsis-end" className="channels-table__ellipsis">...</span>);
            }
            pages.push(
                <button
                    key={totalPages}
                    onClick={() => onPageChange(totalPages)}
                    className="channels-table__page-btn"
                >
                    {totalPages}
                </button>
            );
        }

        return pages;
    };

    return (
        <div className="channels-table">
            <div className="channels-table__wrapper">
                <table className="channels-table__table">
                    <thead>
                        <tr>
                            <th 
                                className="channels-table__header channels-table__header--sortable"
                                onClick={() => handleSortClick('title')}
                            >
                                {language === 'ru' ? 'Название' : 'Title'}
                                <span className="channels-table__sort-icon">
                                    {getSortIcon('title')}
                                </span>
                            </th>
                            <th 
                                className="channels-table__header channels-table__header--sortable"
                                onClick={() => handleSortClick('subscriberCount')}
                            >
                                {language === 'ru' ? 'Подписчики' : 'Subscribers'}
                                <span className="channels-table__sort-icon">
                                    {getSortIcon('subscriberCount')}
                                </span>
                            </th>
                            <th 
                                className="channels-table__header channels-table__header--sortable"
                                onClick={() => handleSortClick('viewCount')}
                            >
                                {language === 'ru' ? 'Просмотры' : 'Views'}
                                <span className="channels-table__sort-icon">
                                    {getSortIcon('viewCount')}
                                </span>
                            </th>
                            <th 
                                className="channels-table__header channels-table__header--sortable"
                                onClick={() => handleSortClick('videoCount')}
                            >
                                {language === 'ru' ? 'Видео' : 'Videos'}
                                <span className="channels-table__sort-icon">
                                    {getSortIcon('videoCount')}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && channels.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="channels-table__loading">
                                    {t('loading') || 'Loading...'}
                                </td>
                            </tr>
                        ) : channels.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="channels-table__empty">
                                    {language === 'ru' ? 'Каналы не найдены' : 'No channels found'}
                                </td>
                            </tr>
                        ) : (
                            channels.map((channel) => (
                                <tr key={channel.channelId} className="channels-table__row">
                                    <td className="channels-table__cell channels-table__cell--title">
                                        <div className="channels-table__channel">
                                            {channel.imageWebUrl && (
                                                <img
                                                    src={channel.imageWebUrl}
                                                    alt={channel.title}
                                                    className="channels-table__channel-image"
                                                    width="40"
                                                    height="40"
                                                />
                                            )}
                                            <a
                                                href={`https://www.youtube.com/channel/${channel.channelId}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="channels-table__channel-link"
                                            >
                                                {channel.title}
                                            </a>
                                        </div>
                                    </td>
                                    <td className="channels-table__cell">
                                        {formatNumber(channel.subscriberCount)}
                                    </td>
                                    <td className="channels-table__cell">
                                        {formatNumber(channel.viewCount)}
                                    </td>
                                    <td className="channels-table__cell">
                                        {formatNumber(channel.videoCount)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {pagination.totalPages > 1 && (
                <div className="channels-table__pagination">
                    <button
                        onClick={() => onPageChange(pagination.page - 1)}
                        disabled={pagination.page === 1 || loading}
                        className="channels-table__page-btn channels-table__page-btn--nav"
                    >
                        {language === 'ru' ? '← Назад' : '← Previous'}
                    </button>
                    
                    <div className="channels-table__page-numbers">
                        {renderPagination()}
                    </div>

                    <button
                        onClick={() => onPageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages || loading}
                        className="channels-table__page-btn channels-table__page-btn--nav"
                    >
                        {language === 'ru' ? 'Вперед →' : 'Next →'}
                    </button>
                </div>
            )}

            {pagination.totalItems > 0 && (
                <div className="channels-table__info">
                    {language === 'ru' 
                        ? `Показано ${(pagination.page - 1) * pagination.limit + 1}-${Math.min(pagination.page * pagination.limit, pagination.totalItems)} из ${pagination.totalItems}`
                        : `Showing ${(pagination.page - 1) * pagination.limit + 1}-${Math.min(pagination.page * pagination.limit, pagination.totalItems)} of ${pagination.totalItems}`
                    }
                </div>
            )}
        </div>
    );
});

export default ChannelsTable;

